import { type PropsWithChildren } from 'react';

import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react';
import { differenceInYears } from 'date-fns';

import { CenteredLoadingIndicator } from '@storebrand-digital/bank-foundation-components';

import { useCustomer } from '@api/hooks/useCustomer';
import useIsBankCustomer from '@api/hooks/useIsBankCustomer';
import { FeatureFlags } from '@app/types/enums';

import AgeRedirectorGuard from './AgeRedirectorGuard';
import DeprecatedRouteRedirector from './DeprecatedRouteRedirector';
import ProductRedirectorGuard from './ProductRedirectorGuard';
import UnderMaintenanceGuard from './UnderMaintenanceGuard';

export default function Guard({ children }: PropsWithChildren) {
  const isUnderMaintenance = useFlag(FeatureFlags.UNDER_MAINTENANCE);
  const flagsStatus = useFlagsStatus();

  const { data: isBankCustomer, error: isBankCustomerError, isLoading: isBankCustomerLoading } = useIsBankCustomer();
  const { data: customer, error: customerError, isLoading: isCustomerLoading } = useCustomer();

  const isFlagsStatusLoading = !flagsStatus.flagsReady && !flagsStatus.flagsError;

  const isLoading = [isFlagsStatusLoading, isBankCustomerLoading, isCustomerLoading].some(Boolean);

  if (isLoading) {
    return <CenteredLoadingIndicator isFixed />;
  }

  const customerAge = customer?.dateOfBirth?.value
    ? differenceInYears(Date.now(), new Date(customer.dateOfBirth.value))
    : null;

  return (
    <UnderMaintenanceGuard isUnderMaintenance={flagsStatus.flagsError ? null : isUnderMaintenance}>
      <DeprecatedRouteRedirector>
        <AgeRedirectorGuard customerAge={customerError ? null : customerAge}>
          <ProductRedirectorGuard isBankCustomer={isBankCustomerError ? null : isBankCustomer}>
            {children}
          </ProductRedirectorGuard>
        </AgeRedirectorGuard>
      </DeprecatedRouteRedirector>
    </UnderMaintenanceGuard>
  );
}
